// GalleryUIFunctions.tsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HLSPlayer } from "./GalleryUIMobile";
import LazyMedia from "./LazyMediaMobile";
import FollowButtonInline from "../../components/FollowButtonMobile";
import { useTranslation } from "react-i18next";
import Modal from "../../components/ModalReport";
import api from "../../../shared/api/axios";

/** ——— Types (structural typing means these can mirror your Gallery.tsx types) ——— */

export type MediaEntry = {
  kind: 'image' | 'video';

  // image
  url?: string;

  // new video pipeline
  isHls?: boolean;
  videoId?: string | null;
  hlsUrl?: string | null;
  dashUrl?: string | null;
  posterUrl?: string | null;

  // music
  musicTitle?: string | null;
  musicArtist?: string | null;
  musicId?: string | null;
};

export interface Post {
  _id: string;
  media: MediaEntry[];  // This is the key part, media should be an array of MediaEntry
  captions: string[];
  createdAt: string;
  likes: number;
  likedByUser?: boolean;
  location?: {
    city: string;
    country: string;
    countryCode?: string;
  };
  user: {
    _id: string;
    profilename: string;
    picture?: string | null;
  } | null;
  totalLikes?: number;
  likeKey?: string;
  hashtags?: string[];
  status?: string;
}

interface MediaItemProps {
  index: number;
  media: MediaEntry; // This should match your mapping structure
  isActive: boolean;
  globalMuted: boolean;
  registerChild: (el: HTMLDivElement | null, i: number) => void;
  onMusicClick?: (entry: MediaEntry) => void;
}
/** ——— Local helpers/consts used by the UI components ——— */
  
// Api base URL normalization
const API_BASE = process.env.REACT_APP_API_BASE_URL as string;

if (!API_BASE) {
  throw new Error("REACT_APP_API_BASE_URL is not defined");
}

const API_ORIGIN = API_BASE.replace(/\/api(\/)?$/, "").replace(/\/$/, "");

const IMAGE_CDN = process.env.REACT_APP_PUBLIC_IMAGE_BASE_URL as string;

if (!IMAGE_CDN) {
  throw new Error("REACT_APP_PUBLIC_IMAGE_BASE_URL is not defined");
}

const resolveVideoUrl = (m: MediaEntry): string => {
  if (m.hlsUrl) return m.hlsUrl;
  if (m.videoId) return `${API_ORIGIN}/hls/playlist/${m.videoId}`;
  if (m.url && /^https?:\/\//i.test(m.url)) return m.url; // legacy fallback
  return "";
};

// Media sizing
const MUSIC_BAR_HEIGHT = 38;
const MEDIA_BOX = `calc(100svh - 108px - ${MUSIC_BAR_HEIGHT}px)`;
const MEDIA_HEIGHT = MEDIA_BOX;

// Styles (duplicated locally so Gallery.tsx doesn't need changes)
const postCardStyle: React.CSSProperties = {
  background:
  'linear-gradient(145deg, rgba(6, 11, 23, 0.95), rgba(4, 7, 15, 0.95))',  padding: "0",
  borderRadius: 0,
  marginBottom: 0,
  boxShadow: "none",
  color: "#888",
};

const mediaScrollContainerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  overflowX: "auto",
  overflowY: "hidden",
  height: MEDIA_HEIGHT,
  alignItems: "center",
  gap: 8,
  paddingRight: 5,
  transform: "translateX(-8px)",
  scrollSnapType: "x proximity",
  scrollSnapStop: "always",
  overscrollBehaviorX: "contain",
  contain: "layout paint size",
  WebkitOverflowScrolling: "touch",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  touchAction: "auto",
};


// Asset utils (lightweight, module-scoped)
const VIDEO_EXT_RE = /\.(mp4|mov|m4v|webm)$/i;
const isVideoUrl = (u: string) => {
  try {
    const pathname = new URL(u, window.location.origin).pathname.toLowerCase();
    return (
      VIDEO_EXT_RE.test(pathname) ||
      pathname.endsWith(".m3u8") ||
      pathname.includes("/hls/") ||
      pathname.includes("/playlist/")
    );
  } catch {
    const s = u.toLowerCase();
    return (
      VIDEO_EXT_RE.test(s) ||
      s.endsWith(".m3u8") ||
      s.includes("/hls/") ||
      s.includes("/playlist/")
    );
  }
};

const seenPrefetch = new Set<string>();

const prefetchAsset = (url?: string) => {
  if (!url || seenPrefetch.has(url)) return;

  if (isVideoUrl(url)) {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.src = url;
    try {
      v.load();
    } catch {}
  } else {
    const img = new Image();
    img.decoding = "async";
    img.loading = "eager";
    img.src = url;
  }

  try {
    const link = document.createElement("link");
    link.rel = "prefetch";
    link.href = url;
    document.head.appendChild(link);
  } catch {}

  seenPrefetch.add(url);
};

/** ——— Hook: useMediaOrientation ———
 * Determines if a media URL (image/video) is landscape. For videos, reads metadata.
 */
export function useMediaOrientation(
  url: string,
  isVideo: boolean,
  onResult: (isLandscape: boolean | null) => void
) {
  React.useEffect(() => {
    let alive = true;

    if (!url) {
      onResult(null);
      return;
    }

    if (isVideo) {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.src = url;

      const handle = () => {
        if (!alive) return;
        const w = v.videoWidth || 0;
        const h = v.videoHeight || 0;
        onResult(w && h ? w > h : null);
        cleanup();
      };
      const err = () => {
        if (alive) onResult(null);
        cleanup();
      };
      const cleanup = () => {
        v.removeEventListener("loadedmetadata", handle);
        v.removeEventListener("error", err);
      };

      v.addEventListener("loadedmetadata", handle);
      v.addEventListener("error", err);
      try {
        v.load();
      } catch {}

      return () => {
        alive = false;
        cleanup();
      };
    } else {
      onResult(null);
    }
  }, [url, isVideo, onResult]);
}

/** ——— Component: ImageAutoFit ———
 * Loads the image to detect orientation, then applies portrait/landscape styles.
 */
export function ImageAutoFit({
  src,
  alt,
  portraitStyle,
  landscapeStyle,
  outerPortraitStyle,
  outerLandscapeStyle,
  onOrientationChange,
  onClick,
  clickable,
}: {
  src: string;
  alt?: string;
  portraitStyle: React.CSSProperties;
  landscapeStyle: React.CSSProperties;
  outerPortraitStyle?: React.CSSProperties;
  outerLandscapeStyle?: React.CSSProperties;
  onOrientationChange?: (isLandscape: boolean | null) => void;
  onClick?: () => void;
  clickable?: boolean;
}) {
  const [isLandscape, setIsLandscape] = React.useState<boolean | null>(null);
  const { t } = useTranslation();

  React.useEffect(() => {
    let alive = true;
    const img = new Image();
    img.onload = () => {
      if (!alive) return;
      const w = img.naturalWidth || 0;
      const h = img.naturalHeight || 0;
      const val = w && h ? w > h : null;
      setIsLandscape(val);
      onOrientationChange?.(val ?? null);
    };
    img.onerror = () => {
      if (alive) {
        setIsLandscape(null);
        onOrientationChange?.(null);
      }
    };
    img.src = src;
    return () => {
      alive = false;
    };
  }, [src, onOrientationChange]);

  const style = isLandscape ? landscapeStyle : portraitStyle;
  const outerStyle = isLandscape ? outerLandscapeStyle : outerPortraitStyle;

  return (
    <div style={outerStyle}>
      <LazyMedia
        type="img"
        src={src}
        alt={alt || t('gallery.media.previewAlt')}
        style={{
          ...style,
          cursor: clickable ? "pointer" : undefined,
        }}
        onClick={onClick}
      />
    </div>
  );
}

/** ——— Component: MediaItem ———
 * One tile inside a post’s horizontal gallery. Handles image vs video rendering,
 * hover overlay for music, and orientation styles for images.
 */
export function MediaItem({
  index,
  media,
  isActive,
  globalMuted,
  registerChild,
  onMusicClick,
}: MediaItemProps) {
  const { t } = useTranslation();

  const isVideo =
  media.kind === "video" &&
  !!(media.hlsUrl || media.videoId || media.url);

const mediaUrl = isVideo
  ? resolveVideoUrl(media)
  : (media.url || "");

  const musicLabel = [media.musicTitle, media.musicArtist]
    .filter(Boolean)
    .join(" — ");


  return (
    <div
      ref={(el) => registerChild(el, index)}
      style={{
        position: "relative",
        height: MEDIA_HEIGHT,
        width: "100vw",
        maxWidth: "100vw",
        flex: "0 0 100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        scrollSnapAlign: "start",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {/* COLUMN LAYOUT */}
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        {/* VIDEO */}
        {isVideo && mediaUrl && (
          <>
            <div
              style={{
                width: "100%",
                flex: "1 1 auto", // video fills remaining height
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              <HLSPlayer
                src={mediaUrl}
                muted={globalMuted}
                active={isActive}
                style={{
                  height: "100%",
                  width: "100%",
                  objectFit: "contain",
                  borderRadius: 12,
                }}
              />
            </div>

            {/* MUSIC UNDER VIDEO */}
            {(media.musicTitle || media.musicArtist) && (
              <div
                onClick={() => onMusicClick?.(media)}
                title={musicLabel}
                style={{
                  height: 26,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#7a7676",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  padding: "0 8px",
                  maxWidth: "92%",
                  marginTop: 4,
                }}
              >
                🎵 {musicLabel}
              </div>
            )}
          </>
        )}

        {/* IMAGE */}
        {!isVideo && mediaUrl && (
          <ImageAutoFit
            src={mediaUrl}
            alt={t('gallery.media.previewAlt')}
            portraitStyle={{
              height: MEDIA_HEIGHT,
              width: "100vw",
              maxWidth: "100vw",
              flex: "0 0 100vw",
              objectFit: "contain",
              borderRadius: 12,
            }}
            landscapeStyle={{
              height: MEDIA_HEIGHT,
              width: "100vw",
              maxWidth: "100vw",
              flex: "0 0 100vw",
              objectFit: "contain",
              borderRadius: 12,
            }}
            outerPortraitStyle={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
            outerLandscapeStyle={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
            clickable
          />
        )}
      </div>
    </div>
  );
}

/** ——— Component: PostSection ———
 * Complete vertical page section for a single post (header, horizontal media strip, footer).
 */
export function PostSection({
  post,
  isActive,
  inWindow,
  globalMuted,
  navigate,
  onLike,
  onUnlike,
  currentUserId,
  onMusicClick,
}: {
  post: Post;
  isActive: boolean;
  inWindow: boolean;
  globalMuted: boolean;
  navigate: ReturnType<typeof useNavigate>;
  onLike: (id: string) => void;
  onUnlike: (id: string) => void;
  currentUserId?: string;
  onMusicClick?: (entry: MediaEntry) => void; 
}) {
  const { t } = useTranslation();
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const itemRefs = React.useRef<HTMLDivElement[]>([]);
  const [currentIdx, setCurrentIdx] = React.useState(0);

  // Prefetch neighbor media inside this post
  React.useEffect(() => {
  const next = post.media[currentIdx + 1]?.url;
  const prev = post.media[currentIdx - 1]?.url;
  if (next) prefetchAsset(next);
  if (prev) prefetchAsset(prev);
}, [currentIdx, post.media]);

const [menuOpen, setMenuOpen] = useState(false);
const [reportOpen, setReportOpen] = useState(false);
const [selectedReason, setSelectedReason] = useState<string | null>(null);
const [reportStep, setReportStep] = useState<"list" | "details" | "done">("list");
const [details, setDetails] = useState("");

const menuRef = useRef<HTMLDivElement | null>(null);

useEffect(() => {
  const handleClickOutside = (e: MouseEvent | TouchEvent) => {
    if (!menuRef.current) return;
    if (!menuRef.current.contains(e.target as Node)) {
      setMenuOpen(false);
    }
  };

  if (menuOpen) {
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
  }

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
    document.removeEventListener("touchstart", handleClickOutside);
  };
}, [menuOpen]);

const handleReportPost = () => {
  setMenuOpen(false);
  setReportOpen(true);
  setReportStep("list");
};

const submitReport = async () => {
  if (!selectedReason) return;

  try {
    await api.post("/block-report/report/post", {
      postId: post._id,
      reasonKey: selectedReason,
      details: details || undefined,
    });

    setReportStep("done");

    setTimeout(() => {
      setReportOpen(false);
      setSelectedReason(null);
      setDetails("");
      setReportStep("list");
    }, 1200);
  } catch (err) {
    console.error(err);
    alert(t('gallery.report.failed'));
  }
};

  // Profile navigation
  const ownerId = post.user?._id;
  const isOwnPost = !!ownerId && currentUserId === ownerId;
  const goToAuthor = () => {
    if (!ownerId) return;
    navigate(isOwnPost ? "/profile" : `/visitor-profile/${ownerId}`);
  };

  const goChatWithAuthor = () => {
  if (!post.user?._id || isOwnPost) return;

  setMenuOpen(false);

  navigate("/chat", {
    state: {
      toUserId: post.user._id,
      toUserName: post.user.profilename || "",
      toUserPicture: post.user.picture || "",
    },
  });
};

const openPostViewer = (post: Post, startIndex = 0) => {
  const viewerMedia = post.media
    .map((m) => {
      if (m.kind === "image") {
        if (!m.url) return null;

        let finalUrl = m.url;

        if (finalUrl.startsWith("http")) {
          return {
            kind: "image",
            url: finalUrl,
          };
        }

        if (finalUrl.startsWith("/")) {
          return {
            kind: "image",
            url: `${IMAGE_CDN}${finalUrl}`,
          };
        }

        return {
          kind: "image",
          url: `${IMAGE_CDN}/${finalUrl}`,
        };
      }

      const videoUrl = resolveVideoUrl(m);
      if (!videoUrl) return null;

      return {
        kind: "video",
        url: videoUrl,
        posterUrl: m.posterUrl ?? null,
        musicId: m.musicId ?? null,
        musicTitle: m.musicTitle ?? null,
        musicArtist: m.musicArtist ?? null,
      };
    })
    .filter(Boolean);

  navigate("/openviewermd", {
    state: {
      media: viewerMedia,
      startIndex,
    },
  });
};

  const registerChild = (el: HTMLDivElement | null, i: number) => {
    if (el) itemRefs.current[i] = el;
  };

  // Track most-visible child in the horizontal scroller
  React.useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const io = new IntersectionObserver(
      (entries) => {
        let best: { idx: number; ratio: number } | null = null;
        for (const e of entries) {
          const idx = itemRefs.current.findIndex((n) => n === e.target);
          if (idx >= 0) {
            const r = e.intersectionRatio;
            if (!best || r > best.ratio) best = { idx, ratio: r };
          }
        }
        if (best && best.ratio > 0) {
          requestAnimationFrame(() => setCurrentIdx(best!.idx));
        }
      },
      { root, threshold: [0.65]}
    );

    itemRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [post._id]);

  // Header right side info
const countryCode = post.location?.countryCode?.toUpperCase();
const translatedCountry = countryCode
  ? t(`countries.${countryCode}`, {
      defaultValue: post.location?.country || countryCode,
    })
  : post.location?.country || "";

const locationLabel =
  post.location?.city && translatedCountry
    ? `${post.location.city}, ${translatedCountry}`
    : translatedCountry
    ? translatedCountry
    : "";

  const dateLabel = t("gallery.post.postedAt", {
    date: new Date(post.createdAt).toLocaleString(),
  });

  return (
    <section
      data-post-id={post._id}
      style={{
        ...postCardStyle,
        height: "100dvh",
        boxSizing: "border-box",
        scrollSnapAlign: "start",
        scrollSnapStop: "always",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        marginBottom: 0,
      }}
    >
      {/* SINGLE HEADER ROW */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          justifyContent: "space-between",
          flexWrap: "wrap", // still allows wrapping on very small screens
          marginLeft: 25,
marginBottom: 3,
marginTop: 16,
        }}
      >
        {/* LEFT: avatar + name + follow */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            minWidth: 0,
          }}
        >
          <div
            onClick={goToAuthor}
            style={{
              width: 35,
              height: 35,
              borderRadius: "5%",
              backgroundColor: post.user?.picture ? "transparent" : "rgb(38, 29, 162)",
              backgroundImage: post.user?.picture ? `url(${post.user.picture})` : "none",
              backgroundSize: "cover",
              backgroundPosition: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: post.user?.picture ? 0 : "20px",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            {!post.user?.picture && "👻"}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              minWidth: 0,
            }}
          >
            <span
              onClick={goToAuthor}
              style={{
                cursor: "pointer",
                color: "#bbb",
                fontWeight: 600,
                fontSize: 15,
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
                maxWidth: 220,
              }}
            >
              {post.user?.profilename || t("gallery.post.unknownUser")}
            </span>

<span style={{ display: "inline-flex", flexShrink: 0, gap: 6 }}>
  <FollowButtonInline
    authorId={post.user?._id}
    profilename={post.user?.profilename}
  />

  <div
    ref={menuRef}
    style={{ position: "relative" }}
    onClick={(e) => e.stopPropagation()}
  >
    <button
      onClick={(e) => {
        e.stopPropagation();
        setMenuOpen((v) => !v);
      }}
      style={{
        background: "transparent",
        border: "none",
        color: "#bbb",
        fontSize: 16,
        cursor: "pointer",
        padding: "2px 6px",
      }}
    >
      ⋯
    </button>

    {menuOpen && (
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          left: "100%",
          top: "50%",
          transform: "translateY(-50%)",
          marginLeft: 6,
          background: "#111",
          border: "1px solid #333",
          borderRadius: 6,
          padding: "3px 5px",
          zIndex: 50,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          whiteSpace: "nowrap",
          gap: 6,
          boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
        }}
      >
        <button
  onClick={(e) => {
    e.stopPropagation();
    e.preventDefault();
    goChatWithAuthor();
  }}
  style={{
    backgroundColor: "#111",
    color: "#bbb",
    border: "1px solid #D4AF37",
    borderRadius: "6px",
    padding: "4px 10px",
    minWidth: "85px",
    textAlign: "center",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: 500,
  }}
>
  💬 {t("MyLikes.buttons.chat")}
</button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleReportPost();
          }}
          style={{
            padding: "3px 6px",
            textAlign: "left",
            background: "none",
            border: "none",
            color: "#ff4d4d",
            cursor: "pointer",
            fontSize: 11,
            lineHeight: 1.1,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            whiteSpace: "nowrap",
            borderRadius: 4,
          }}
        >
          🚩 {t('gallery.report.button')}
        </button>
      </div>
    )}
  </div>
</span>
          </div>
        </div>

        {/* RIGHT: location + date + buttons — all one line */}
      <div
  style={{
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    justifyContent: "flex-end",
    fontSize: 9,
  }}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      color: "#555",
      whiteSpace: "nowrap",
    }}
  >
    {locationLabel && (
  <button
    onClick={() => {
      const city = post.location?.city?.trim();
      const country = post.location?.country?.trim();
      const countryCode = post.location?.countryCode?.trim();

      if (!city || !(countryCode || country)) return;

      navigate(
        `/location-collection?city=${encodeURIComponent(city)}&country=${encodeURIComponent(
          country || ""
        )}&countryCode=${encodeURIComponent(countryCode || "")}`
      );
    }}
    style={{
      background: "transparent",
      border: "none",
      padding: 0,
      margin: 0,
      color: "#555",
      cursor: "pointer",
      fontSize: 13,
      whiteSpace: "nowrap",
    }}
    title={locationLabel}
  >
    📍 {locationLabel}
  </button>
)}
    <span>{dateLabel}</span>
  </div>

  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexShrink: 0,
      minWidth: 0,
      marginRight: 10,
      marginTop: -2,
    }}
  >
    {/* 🎬 Presentation */}
    <button
      onClick={() => openPostViewer(post, currentIdx)}
      disabled={!post.media || post.media.length === 0}
      title={t('myPosts.meta.openViewer')}
      style={{
        backgroundColor: "#000",
        color: "#bbb",
        border: "1px solid #333",
        borderRadius: 6,
        padding: "3px 8px",
        fontSize: 12,
        cursor: post.media?.length ? "pointer" : "not-allowed",
        opacity: post.media?.length ? 1 : 0.5,
        flexShrink: 0,
      }}
    >
      🎬 {t('myPosts.meta.presentation')}
    </button>

    {/* ❤️ Like */}
    <button
      onClick={() =>
        post.likedByUser ? onUnlike(post._id) : onLike(post._id)
      }
      style={{
        backgroundColor: post.likedByUser ? "#ffccd5" : "#111",
        color: post.likedByUser ? "#a00" : "#bbb",
        border: "1px solid #ccc",
        borderRadius: "6px",
        padding: "3px 5px",
        fontSize: "12px",
        cursor: "pointer",
        transition: "all 0.3s ease",
        flexShrink: 0,
      }}
    >
      {post.likedByUser
        ? `💔 Unlike (${post.likes})`
        : `❤️ Like (${post.likes})`}
    </button>
  </div>
</div>

      </div>

      {/* MEDIA AREA (bigger, no big footer) */}
      {inWindow ? (
        <div ref={containerRef} style={mediaScrollContainerStyle}>
          {post.media.length === 0 ? (
            <p style={{ color: "#bbb" }}>{t("gallery.post.noMedia")}</p>
          ) : (
            post.media.map((m, index) => (
              <MediaItem
                key={`${m.videoId || m.hlsUrl || m.url || index}`}
                index={index}
                media={m}
                 isActive={isActive && index === currentIdx}
                globalMuted={globalMuted}
                registerChild={registerChild}
                onMusicClick={onMusicClick}
              />
            ))
          )}
        </div>
      ) : (
        <div
          style={{
            height: MEDIA_HEIGHT,
            borderRadius: 12,
            background: "#050505",
          }}
        />
      )}

      {/* tiny spacer if you want a bit of breathing room */}

      <Modal
  open={reportOpen}
  onClose={() => {
    setReportOpen(false);
    setSelectedReason(null);
    setDetails("");
    setReportStep("list");
  }}
  title={t('gallery.report.title')}
  width={380}
>
  {reportStep === "list" && (
    <>
      {[
  { key: "spam", label: t('gallery.report.reasons.spam') },
  { key: "hate", label: t('gallery.report.reasons.hate') },
  { key: "harassment", label: t('gallery.report.reasons.harassment') },
  { key: "sexual content", label: t('gallery.report.reasons.sexualContent') },
  { key: "scam or fraud", label: t('gallery.report.reasons.scamOrFraud') },
  { key: "violence", label: t('gallery.report.reasons.violence') },
  { key: "self-harm", label: t('gallery.report.reasons.selfHarm') },
  { key: "other", label: t('gallery.report.reasons.other') },
].map((r) => (
        <button
          key={r.key}
          onClick={() => {
            setSelectedReason(r.key);
            setReportStep("details");
          }}
          style={{
            width: "100%",
            padding: "8px 10px",
            background: "transparent",
            border: "none",
            borderBottom: "1px solid #222",
            color: "#ddd",
            textAlign: "left",
            cursor: "pointer",
            fontSize: 13,
          }}
        >
          {r.label}
        </button>
      ))}
    </>
  )}

  {reportStep === "details" && (
    <>
      <textarea
        value={details}
        onChange={(e) => setDetails(e.target.value)}
        placeholder={t('gallery.report.detailsPlaceholder')}
        style={{
          width: "100%",
          minHeight: 70,
          borderRadius: 6,
          padding: 8,
          background: "#000",
          border: "1px solid #333",
          color: "#fff",
          marginBottom: 10,
          fontSize: 13,
        }}
      />

      <button
        onClick={submitReport}
        style={{
          width: "100%",
          padding: "8px",
          background: "#ff4d4d",
          border: "none",
          borderRadius: 6,
          color: "#fff",
          cursor: "pointer",
          fontSize: 13,
        }}
      >
        {t('gallery.report.submit')}
      </button>
    </>
  )}

  {reportStep === "done" && (
    <div style={{ textAlign: "center", fontSize: 14 }}>
     ✅ {t('gallery.report.submitted')}
    </div>
  )}
</Modal>
    </section>
  );
}



export default {
  useMediaOrientation,
  ImageAutoFit,
  MediaItem,
  PostSection,

};
